import React, { useState, useEffect, useRef } from 'react'
import { ConfigProvider, Tour, theme } from 'antd'
import type { TourStepProps } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import {
  currentUserRoleSelector,
  currentUserTourCompletedSelector,
  currentUserSelector,
  currentUserTermsConditionsSelector
} from '../../redux/session/currentUser/selectors'
import { contextCompanyIdSelector } from '../../redux/context/company/selectors'
import { getTourSteps } from './intro/steps'
import { tourCompletedRequest } from '../../redux/session/currentUser/actions'
import { useLoggedin, usePermission } from '../../redux/session/currentUser/hooks'
import { AppDispatch } from '../../redux/store'

const UserTour: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [steps, setSteps] = useState<TourStepProps[]>([])
  const role = useSelector(currentUserRoleSelector)
  const isLoggedIn = useLoggedin()
  const tourCompleted = useSelector(currentUserTourCompletedSelector)
  const currentUser = useSelector(currentUserSelector)
  const termsConditionsAgreed = useSelector(currentUserTermsConditionsSelector)
  const writeBudgetsPermission = usePermission('write-budgets')
  const companyId = useSelector(contextCompanyIdSelector)
  const dispatch: AppDispatch = useDispatch()
  const closedRef = useRef(false)

  // Closing from the X counts as done too, so the tour doesn't come back on every page load
  const completeTour = () => {
    setIsOpen(false)
    if (closedRef.current) return
    closedRef.current = true
    dispatch(tourCompletedRequest())
  }

  const tourHandler = () => {
    if (closedRef.current) return
    if (isLoggedIn && !tourCompleted && termsConditionsAgreed && companyId) {
      if (role.label.includes('accountant')) {
        setIsOpen(true)
        setSteps(getTourSteps('accountant'))
      } else if (role.label.includes('company')) {
        setIsOpen(true)
        setSteps(getTourSteps(writeBudgetsPermission ? 'company' : 'companyLite'))
      }
    }
  }

  useEffect(tourHandler, [
    isLoggedIn,
    tourCompleted,
    termsConditionsAgreed,
    role,
    writeBudgetsPermission,
    companyId,
    currentUser
  ])

  return (
    // The app theme is compact; the tour is read once and reads better at normal size
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <Tour steps={steps} open={isOpen} onClose={completeTour} onFinish={completeTour} />
    </ConfigProvider>
  )
}

export default UserTour
